import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography} from "@mui/material";
import {ActiveDirectoryProperties} from "../../../../../models/admin-config.models";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {adminConfigurationService, DEFAULT_AD_FORM_VALUES,} from "../../../../../services/admin-config.service";
import CircularProgress from "@mui/material/CircularProgress";
import {parseError} from "../../../../../errors/http.service";
import {useSnackbar} from "../../../../../contexts/SnackbarContext";
import {StyledFormButtons, StyledFormFields, StyledFormWrapper} from "./ActiveDirectory.styled";

const ActiveDirectoryConfiguration = () => {
    const {t} = useTranslation();
    const { showSnackbar } = useSnackbar();

    const { register, control, reset, handleSubmit, formState: { errors, isDirty } } = useForm<ActiveDirectoryProperties>({
        defaultValues: DEFAULT_AD_FORM_VALUES,
    });

    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [adCode, setAdCode] = useState<string | null>(null);
    const adCodeRef = useRef<string | null>(null);

    const fetchCode = useCallback(async () => {
        const {code} = await adminConfigurationService.fetchActiveDirectoryCode();
        setAdCode(code);
        adCodeRef.current = code;
    }, []);

    const loadConfig = useCallback(async () => {
        if (!adCodeRef.current) return;

        setIsFetching(true);

        try {
            const adFormData = await adminConfigurationService.fetchActiveDirectoryConnectionConfig(adCodeRef.current);
            reset(adFormData);
        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.loadError'), 'error');
        } finally {
            setIsFetching(false);
        }
    }, [t]);

    useEffect(() => {
        const init = async () => {
            await fetchCode();
            await loadConfig();
        };
        init();
    }, []);

    const onSubmit = async (formData: ActiveDirectoryProperties) => {
        try {
            setIsSaving(true);

            await adminConfigurationService.setActiveDirectoryConnectionConfig(formData);
            reset(formData);
            showSnackbar(t('configuration.updateSuccess'), 'success');

            await fetchCode();
            await loadConfig();
        } catch (err) {
            console.error(err);
            showSnackbar(parseError(err) || t('configuration.updateError'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSynchronization = async () => {
        if (!adCodeRef.current) return;

        try {
            setIsSaving(true);
            await adminConfigurationService.updateActiveDirectoryGroupsWithConnectionConfig(adCodeRef.current);
            showSnackbar(t('configuration.ad.synchronizationSuccess'), 'success');
        } catch (err) {
            console.error(err);
            showSnackbar(t('configuration.ad.synchronizationError'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const formFields = useMemo(() => [
        {
            name: 'certificateFileName' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.certificateFileName.label'),
            placeholder: t('configuration.ad.certificateFileName.placeholder'),
            helperText: t('configuration.ad.certificateFileName.helper'),
            rules: { },
        },
        {
            name: 'certificateContent' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.certificateContent.label'),
            placeholder: t('configuration.ad.certificateContent.placeholder'),
            helperText: t('configuration.ad.certificateContent.helper'),
            rules: { },
        },
        {
            name: 'url' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.url.label'),
            placeholder: t('configuration.ad.url.placeholder'),
            helperText: t('configuration.ad.url.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'code' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.code.label'),
            placeholder: t('configuration.ad.code.placeholder'),
            helperText: t('configuration.ad.code.helper'),
            rules: { required: t('configuration.ad.validation') }
        },
        {
            name: 'baseDn' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.baseDn.label'),
            placeholder: t('configuration.ad.baseDn.placeholder'),
            helperText: t('configuration.ad.baseDn.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'groupSearchBase' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.groupSearchBase.label'),
            placeholder: t('configuration.ad.groupSearchBase.placeholder'),
            helperText: t('configuration.ad.groupSearchBase.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'groupNameAttribute' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.groupNameAttribute.label'),
            placeholder: t('configuration.ad.groupNameAttribute.placeholder'),
            helperText: t('configuration.ad.groupNameAttribute.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'groupMemberAttribute' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.groupMemberAttribute.label'),
            placeholder: t('configuration.ad.groupMemberAttribute.placeholder'),
            helperText: t('configuration.ad.groupMemberAttribute.helper'),
            rules: { required: t('configuration.ad.validation') },
        },

        {
            name: 'userSearchBase' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.userSearchBase.label'),
            placeholder: t('configuration.ad.userSearchBase.placeholder'),
            helperText: t('configuration.ad.userSearchBase.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'memberOfAttribute' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.memberOfAttribute.label'),
            placeholder: t('configuration.ad.memberOfAttribute.placeholder'),
            helperText: t('configuration.ad.memberOfAttribute.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'userLoginAttribute' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.userLoginAttribute.label'),
            placeholder: t('configuration.ad.userLoginAttribute.placeholder'),
            helperText: t('configuration.ad.userLoginAttribute.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'userObjectClass' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.userObjectClass.label'),
            placeholder: t('configuration.ad.userObjectClass.placeholder'),
            helperText: t('configuration.ad.userObjectClass.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'serviceAccountDn' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.serviceAccountDn.label'),
            placeholder: t('configuration.ad.serviceAccountDn.placeholder'),
            helperText: t('configuration.ad.serviceAccountDn.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
        {
            name: 'serviceAccountPassword' as keyof ActiveDirectoryProperties,
            label: t('configuration.ad.serviceAccountPassword.label'),
            placeholder: t('configuration.ad.serviceAccountPassword.placeholder'),
            helperText: t('configuration.ad.serviceAccountPassword.helper'),
            rules: { required: t('configuration.ad.validation') },
        },
    ], [t]);

    return (
        <Stack className="active-directory-configuration"
               component="form"
               onSubmit={handleSubmit(onSubmit)}
               id="ad-config-form"
               direction='column'
               spacing={3}
        >
            {!adCode && (
                <Box display="flex" justifyContent="center" alignItems="center" textAlign="center">
                    <Typography variant="body1" color="error.main">
                        {t('configuration.ad.noCode')}
                    </Typography>
                </Box>
            )}

            <StyledFormWrapper>
                <StyledFormFields>
                    {isFetching && (
                        <Box display="flex" justifyContent="center" alignItems="center" textAlign="center" height="200px">
                            <CircularProgress size={36} />
                        </Box>
                    )}

                    {!isFetching && (
                        <Box display="flex" flexDirection="column" gap={4}>
                            <Alert severity="warning" sx={{maxWidth: {xs: '100%', mdPlus: '500px'}}}>
                                {t('configuration.ad.certificateRestartInfo')}
                            </Alert>

                            <Controller
                                name="nestedGroupsEnabled"
                                control={control}
                                render={({field}) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />
                                        }
                                        label={t('configuration.ad.nestedGroupsEnable')}
                                    />
                                )}
                            />

                            {formFields.map((field) => (
                                <Box key={field.name}>
                                    <Typography variant="body2" sx={{color: 'text.hint'}}>
                                        {field.label}
                                    </Typography>
                                    <TextField {...register(field.name, field.rules)}
                                               error={!!errors[field.name]}
                                               placeholder={field.placeholder}
                                               helperText={field.helperText}
                                               type={field.name === 'serviceAccountPassword' ? 'password' : 'text'}
                                               fullWidth
                                               sx={{maxWidth: {xs: '100%', mdPlus: '500px'}}}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </StyledFormFields>

                <StyledFormButtons>
                    <Button
                        type="button"
                        variant="outlined"
                        disabled={isFetching || isSaving || isDirty || adCode === null}
                        onClick={handleSynchronization}
                    >
                        {isSaving ? <CircularProgress size={16} /> : t('configuration.ad.synchronize')}
                    </Button>

                    <Button
                        type="submit"
                        form="ad-config-form"
                        variant="contained"
                        disabled={isFetching || isSaving || !isDirty || adCode === null}
                    >
                        {isSaving ? <CircularProgress size={16} /> : t('common.save')}
                    </Button>
                </StyledFormButtons>
            </StyledFormWrapper>
        </Stack>
    );
};

export default ActiveDirectoryConfiguration;